import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api, type OperatorOnCallShiftWire } from '../lib/api-client';

/**
 * Hooks da ESCALA de plantão do operador (Frente N N3a-2 — gated
 * `operator-oncall-schedule-read`/`-manage`). CRUD de `OperatorOnCallShift`
 * (operador × vigência). Ativa o regime 3 (plantão fura com `urgent` fora do
 * expediente). Molde: `use-scheduling.ts` (Frente B Plano 2).
 *
 * Seguindo `feedback_gatear_fetch_nao_so_render`, a QUERY é gateada por `enabled`
 * (não só o render) — tenants/operadores sem a cap não disparam fetch (o endpoint
 * é 403 gated). PATCH/DELETE carregam a `version` (If-Match · ADR-071).
 */
export const OPERATOR_ON_CALL_KEY = ['operator-on-call'] as const;

export interface OperatorOnCallFilters {
  readonly userId?: string;
  readonly from?: string;
  readonly to?: string;
  readonly activeAt?: string;
}

export function useOperatorOnCallShifts(
  filters: OperatorOnCallFilters = {},
  enabled = true,
): UseQueryResult<{ data: OperatorOnCallShiftWire[] }, Error> {
  return useQuery({
    queryKey: [...OPERATOR_ON_CALL_KEY, 'shifts', filters],
    queryFn: () => api.getOperatorOnCallShifts(filters),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Mutações da escala (escalar/editar/remover). Em sucesso invalidam o namespace
 * inteiro `['operator-on-call']`. É config de coordenação (rara) → sem cache especial.
 * `update`/`remove` carregam a `version` (If-Match).
 */
export function useOnCallShiftActions(): {
  create: UseMutationResult<
    { data: OperatorOnCallShiftWire },
    Error,
    { userId: string; startsAt: string; endsAt: string; note?: string }
  >;
  update: UseMutationResult<
    { data: OperatorOnCallShiftWire },
    Error,
    { id: string; version: number; startsAt?: string; endsAt?: string; note?: string | null }
  >;
  remove: UseMutationResult<{ data: { deleted: true } }, Error, { id: string; version: number }>;
} {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: OPERATOR_ON_CALL_KEY });
  };
  return {
    create: useMutation({
      mutationFn: (p: Parameters<typeof api.createOperatorOnCallShift>[0]) =>
        api.createOperatorOnCallShift(p),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        version,
        ...body
      }: { id: string; version: number } & Parameters<typeof api.updateOperatorOnCallShift>[2]) =>
        api.updateOperatorOnCallShift(id, version, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (p: { id: string; version: number }) =>
        api.deleteOperatorOnCallShift(p.id, p.version),
      onSuccess: invalidate,
    }),
  };
}
